import React, { useRef } from 'react'
import Icon from 'components/elements/Icon'
import { useDragLayer, DragLayerMonitor } from 'react-dnd'

import { FOLDER_ITEM_TYPES } from 'types/folders'

import styles from './folderDragLayer.module.scss'

/**
 * This component is an invisible layer over the entire Folder Page that renders the drag preview element when dragging
 * is in process. It assigns a top level variable to the ref to its element to allow for style manipulation outside of
 * the react lifecycle. The Drag Preview is simply a folder or a deck icon, depending on the item that renders the name
 * of the item or the number of items if it is a mass drag.
 */
const FolderDragLayer = () => {
  const ref = useRef<HTMLDivElement>(null)

  const layerStyles: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 1,
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  }

  const handleOffsetChange = (monitor: DragLayerMonitor) => {
    if (!ref.current) return

    const offset = monitor.getClientOffset()

    if (!offset) return (ref.current.style.transform = '')

    const transform = `translate(${offset.x}px, ${offset.y}px)`

    ref.current.style.transform = transform
  }

  const { isDragging, item } = useDragLayer(monitor => ({
    item: monitor.getItem(),
    isDragging: monitor.isDragging(),
    onOffsetChange: handleOffsetChange(monitor),
  }))

  if (!isDragging || !item.type) return null

  return (
    <div style={layerStyles} ref={ref}>
      <div>
        <div className={styles.container}>
          <Icon name={item.type === FOLDER_ITEM_TYPES.FOLDER ? 'folder' : 'file'} size="huge" />
          {item.type === FOLDER_ITEM_TYPES.MASS ? (
            <div className={styles.counter}>{item.size}</div>
          ) : (
            <div className={styles.name}>{item.name}</div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FolderDragLayer
